import React, { useCallback, useMemo, useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import { ShoppingListTable } from '@/components/shopping-list/ShoppingListTable';
import { Button, Card, TitleText, BodyText } from '@/components/ui';
import { BackButton } from '@/components/ui/BackButton';
import useShoppingList from '@/hooks/shopping-list/useShoppingList';
import { EmailShoppingListModal } from '@/components/shopping-list/EmailShoppingListModal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

const TrashIcon: React.FC = () => (
  <svg
    className="w-4 h-4"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M19 6l-1 14H6L5 6m3 0V4h8v2m-9 0h10M10 10v6m4-6v6"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EmailIcon: React.FC = () => (
  <svg
    className="w-4 h-4"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M4 6h16v12H4V6zm0 0l8 6 8-6"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RefreshIcon: React.FC = () => (
  <svg
    className="w-4 h-4"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M4 4v6h6"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M20 20v-6h-6"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M20 14a8 8 0 0 0-14-5.3L4 10"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 10a8 8 0 0 0 14 5.3L20 14"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ShoppingListPage: React.FC = () => {
  const {
    shoppingList,
    loading,
    mutating,
    error,
    refresh,
    toggleItemCheck,
    updateQuantity,
    deleteItem,
    clearList,
    emailList,
  } = useShoppingList();

  const [formError, setFormError] = useState<string | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const hasItems = useMemo(() => (shoppingList?.items?.length ?? 0) > 0, [shoppingList?.items]);

  const handleClearList = useCallback(async () => {
    try {
      await clearList();
      setConfirmOpen(false);
    } catch {
      setFormError('Failed to clear the list. Please try again.');
      setConfirmOpen(false);
    }
  }, [clearList]);

  const handleSendEmail = useCallback(async (email: string) => {
    try {
      await emailList(email);
      setEmailModalOpen(false);
    } catch {
      setFormError('Failed to send email. Please try again.');
    }
  }, [emailList]);

  return (
    <Layout className="bg-gray-50 min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-4 mb-4">
          <BackButton fallback="/patient/dashboard" />
        </div>
        <div className="flex items-start justify-between mb-6">
          <div>
            <TitleText>Shopping List</TitleText>
          </div>
          <div className="flex space-x-3">
            <Button
              variant="outline"
              leftIcon={<RefreshIcon />}
              onClick={() => void refresh()}
              disabled={loading || mutating}
            >
              Refresh
            </Button>
            <Button
              variant="primary"
              className="bg-red-600 hover:bg-red-700 active:bg-red-800 focus:ring-red-500"
              leftIcon={<TrashIcon />}
              onClick={() => setConfirmOpen(true)}
              disabled={mutating || !hasItems}
            >
              Clear List
            </Button>
            <Button
              variant="secondary"
              leftIcon={<EmailIcon />}
              onClick={() => setEmailModalOpen(true)}
              disabled={mutating || !hasItems}
            >
              Email
            </Button>
          </div>
        </div>

        {error && (
          <Card className="mb-4 border border-red-200 bg-red-50">
            <BodyText className="text-red-700">{error}</BodyText>
          </Card>
        )}
        {formError && (
          <Card className="mb-4 border border-red-200 bg-red-50">
            <BodyText className="text-red-700">{formError}</BodyText>
          </Card>
        )}

        <ShoppingListTable
          items={shoppingList?.items ?? []}
          onToggleItem={(id, checked) => void toggleItemCheck(id, checked)}
          onQuantityChange={(id, qty) => void updateQuantity(id, qty)}
          onDeleteItem={deleteItem ? (id => void deleteItem(id)) : undefined}
          disabled={mutating}
          isLoading={loading}
        />
        <EmailShoppingListModal
          isOpen={emailModalOpen}
          onClose={() => setEmailModalOpen(false)}
          onSend={handleSendEmail}
          isSubmitting={mutating}
        />
        <ConfirmDialog
          isOpen={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={() => void handleClearList()}
          title="Clear shopping list?"
          message="This will remove all items from your shopping list and cannot be undone."
          confirmText="Clear list"
          cancelText="Cancel"
          isLoading={mutating}
          destructive
        />
      </div>
    </Layout>
  );
};

export default ShoppingListPage;
